import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';

import customerCreditsApi from '../../../api/customerCreditsApi';
import documentLookupApi from '../../../api/documentLookupApi';
import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

export const CREDIT_TERM_OPTIONS = [7, 15, 30];

export const CREDIT_STATUS_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'pagado', label: 'Pagado' },
  { value: 'anulado', label: 'Anulado' },
];

const DOCUMENT_LENGTH = { RUC: 11, DNI: 8 };

export const formatPen = (value) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value || 0);

const schema = yup.object({
  document_type: yup.string().oneOf(['RUC', 'DNI']).required(),
  document_number: yup
    .string()
    .trim()
    .required('Ingresa el número de documento')
    .test('length', 'Número de documento inválido', function validateLength(value) {
      const expected = DOCUMENT_LENGTH[this.parent.document_type];
      return (
        new RegExp(`^\\d{${expected}}$`).test(value || '') ||
        this.createError({ message: `El ${this.parent.document_type} debe tener ${expected} dígitos` })
      );
    }),
  customer_name: yup.string().trim().required('Busca el documento o ingresa el nombre'),
  date: yup.string().required('Selecciona la fecha'),
  term_days: yup.number().oneOf(CREDIT_TERM_OPTIONS).required('Selecciona el plazo'),
  status: yup.string().required('Selecciona el estado'),
  notes: yup.string().trim().nullable(),
});

const today = () => new Date().toLocaleDateString('en-CA');

const emptyValues = {
  document_type: 'RUC',
  document_number: '',
  customer_name: '',
  date: today(),
  term_days: 15,
  status: 'pendiente',
  notes: '',
};

// Alta/edicion de un credito a un cliente: RUC (razon social) o DNI (nombre completo),
// ambos autocompletados con la consulta de documentos (Decolecta), los productos entregados
// con su cantidad y precio, el plazo de pago y el estado.
export default function CustomerCreditFormDialog({ open, onClose, onSaved, credit = null }) {
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);

  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantityInput, setQuantityInput] = useState('1');
  const [priceInput, setPriceInput] = useState('');

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  const isEditing = Boolean(credit);
  const documentType = watch('document_type');
  const documentNumber = watch('document_number');

  useEffect(() => {
    if (open) {
      reset(
        credit
          ? {
              document_type: credit.document_type,
              document_number: credit.document_number,
              customer_name: credit.customer_name,
              date: credit.date,
              term_days: credit.term_days,
              status: credit.status,
              notes: credit.notes || '',
            }
          : { ...emptyValues, date: today() }
      );
      setItems(
        credit?.items?.map((item) => ({
          product: item.product,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })) || []
      );
      setSelectedProduct(null);
      setProductSearch('');
      setQuantityInput('1');
      setPriceInput('');
    }
    // Los valores iniciales solo se leen al abrir el modal, no en cada render del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, credit, reset]);

  // Busca productos conforme el usuario escribe, con un pequeno debounce.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timeout = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const { data } = await productsApi.getProducts({ search: productSearch, per_page: 10, is_active: 1 });
        setProductOptions(data.data);
      } catch (error) {
        // Busqueda secundaria: si falla no se interrumpe el armado del credito.
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [open, productSearch]);

  // Consulta el RUC (razon social) o DNI (nombre completo) y lo pone como nombre del cliente.
  const lookupDocument = async (type, number) => {
    setLookingUp(true);
    try {
      const { data } = await documentLookupApi.lookup(type.toLowerCase(), number);
      if (data.data.name) {
        setValue('customer_name', data.data.name, { shouldValidate: true });
        enqueueSnackbar(`Encontrado: ${data.data.name}`, { variant: 'success' });
      } else {
        enqueueSnackbar('No se encontró información para ese documento, ingresa el nombre a mano.', {
          variant: 'info',
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo consultar el documento.'), { variant: 'error' });
    } finally {
      setLookingUp(false);
    }
  };

  const handleLookupClick = () => {
    const cleaned = (documentNumber || '').trim();
    const expected = DOCUMENT_LENGTH[documentType];
    if (!new RegExp(`^\\d{${expected}}$`).test(cleaned)) {
      setError('document_number', { message: `El ${documentType} debe tener ${expected} dígitos` });
      return;
    }
    lookupDocument(documentType, cleaned);
  };

  // Al completar los digitos del documento se busca automaticamente.
  const handleDocumentNumberChange = (event, field) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, DOCUMENT_LENGTH[documentType]);
    field.onChange(value);
    clearErrors('document_number');
    if (value.length === DOCUMENT_LENGTH[documentType] && value !== documentNumber) {
      lookupDocument(documentType, value);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setPriceInput(product?.price_pen ? String(product.price_pen) : '');
  };

  const canAddItem =
    Boolean(selectedProduct) && Number(quantityInput) >= 1 && priceInput !== '' && Number(priceInput) >= 0;

  const handleAddItem = () => {
    if (!canAddItem) {
      return;
    }
    setItems((prev) => [
      ...prev,
      { product: selectedProduct, quantity: parseInt(quantityInput, 10), unit_price: Number(priceInput) },
    ]);
    setSelectedProduct(null);
    setProductSearch('');
    setQuantityInput('1');
    setPriceInput('');
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const total = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);

  const onSubmit = async (data) => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto.', { variant: 'warning' });
      return;
    }

    const payload = {
      document_type: data.document_type,
      document_number: data.document_number,
      customer_name: data.customer_name,
      date: data.date,
      term_days: Number(data.term_days),
      status: data.status,
      notes: data.notes || undefined,
      items: items.map((item) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.unit_price,
      })),
    };

    try {
      if (isEditing) {
        await customerCreditsApi.updateCredit(credit.id, payload);
      } else {
        await customerCreditsApi.createCredit(payload);
      }
      enqueueSnackbar(isEditing ? 'Crédito actualizado correctamente.' : 'Crédito registrado correctamente.', {
        variant: 'success',
      });
      onSaved();
      onClose();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el crédito.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Editar crédito' : 'Nuevo crédito'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="customer-credit-form" spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller
                name="document_type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Tipo"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValue('document_number', '');
                      setValue('customer_name', '');
                      clearErrors(['document_number', 'customer_name']);
                    }}
                  >
                    <MenuItem value="RUC">RUC (empresa)</MenuItem>
                    <MenuItem value="DNI">DNI (persona)</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="document_number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={documentType}
                    placeholder={documentType === 'RUC' ? '11 dígitos' : '8 dígitos'}
                    onChange={(e) => handleDocumentNumberChange(e, field)}
                    error={Boolean(errors.document_number)}
                    helperText={errors.document_number?.message}
                    slotProps={{
                      htmlInput: { inputMode: 'numeric' },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleLookupClick} disabled={lookingUp}>
                              {lookingUp ? <CircularProgress size={18} /> : <SearchIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <Controller
                name="customer_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={documentType === 'RUC' ? 'Razón social' : 'Nombre completo'}
                    error={Boolean(errors.customer_name)}
                    helperText={errors.customer_name?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle1">Productos</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                sx={{ flex: 3 }}
                options={productOptions}
                value={selectedProduct}
                loading={searchingProducts}
                getOptionLabel={(option) => (option ? `${option.name} (${option.sku})` : '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => handleSelectProduct(value)}
                onInputChange={(event, value) => setProductSearch(value)}
                renderInput={(params) => <TextField {...params} label="Producto" placeholder="Buscar por nombre" />}
              />
              <TextField
                sx={{ flex: 1 }}
                type="number"
                label="Cantidad"
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
              />
              <TextField
                sx={{ flex: 1.3 }}
                type="number"
                label="Precio unit."
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
                slotProps={{
                  htmlInput: { min: 0, step: '0.01' },
                  input: { startAdornment: <InputAdornment position="start">S/</InputAdornment> },
                }}
              />
              <Button variant="outlined" onClick={handleAddItem} disabled={!canAddItem} sx={{ whiteSpace: 'nowrap' }}>
                Agregar
              </Button>
            </Stack>

            {items.length === 0 ? (
              <Alert severity="info">Aún no agregaste productos.</Alert>
            ) : (
              <>
                <List disablePadding>
                  {items.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ListItem
                      key={`${item.product.id}-${index}`}
                      divider
                      disableGutters
                      secondaryAction={
                        <IconButton size="small" onClick={() => removeItem(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={item.product.name}
                        secondary={`${item.quantity} x ${formatPen(item.unit_price)} = ${formatPen(
                          item.quantity * item.unit_price
                        )}`}
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="subtitle1" align="right">
                  Total: {formatPen(total)}
                </Typography>
              </>
            )}
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.date)}
                helperText={errors.date?.message}
                {...register('date')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="term_days"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Plazo"
                    error={Boolean(errors.term_days)}
                    helperText={errors.term_days?.message}
                  >
                    {CREDIT_TERM_OPTIONS.map((days) => (
                      <MenuItem key={days} value={days}>
                        {days} días
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Estado"
                    error={Boolean(errors.status)}
                    helperText={errors.status?.message}
                  >
                    {CREDIT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          <TextField fullWidth multiline minRows={2} label="Observaciones (opcional)" {...register('notes')} />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button type="submit" form="customer-credit-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Registrar crédito'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CustomerCreditFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  credit: PropTypes.object,
};
